import * as yup from 'yup';
import { FC, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useForm, yupResolver } from '@mantine/form';
import { Flex, Space, Text } from '@mantine/core';
import { Auth } from 'aws-amplify';
import { Link, PasswordInput } from '@common/components';
import { validationPattern } from '@common/helpers';
import { useFormFocus } from '@common/hooks';
import { AuthFormHoc } from 'src/hoc';
import { IPersonalInfoValues, IPasswordValues, IAuthValues } from '@common/types/types';
import { PATHS } from 'src/constants';
import { PRIMARY_ACCOUNT_TYPE } from '@common/constants/primaryAccountType';
import { ChevronLeft } from '@geist-ui/icons';
import styles from './styles.module.scss';

interface Props {
  personalInfo: IPersonalInfoValues;
  setIsPersonalInfoFormEnabled: (value: boolean) => void;
}

const initialValues: IPasswordValues = {
  password: '',
  confirmPassword: '',
};

const validationFormSchema = yup.object().shape({
  password: validationPattern.password,
  confirmPassword: validationPattern.confirmPassWord,
});

const PasswordForm: FC<Props> = ({ personalInfo, setIsPersonalInfoFormEnabled }: Props): JSX.Element => {
  const navigate = useNavigate();
  const [t] = useTranslation('auth');
  const [n] = useTranslation('nav');
  const [isLoading, setIsLoading] = useState<boolean>();

  const form = useForm({
    initialValues: initialValues,
    validate: yupResolver(validationFormSchema),
  });
  const { setNeedFocus } = useFormFocus({ formErrors: form.errors });
  const [systemError, setSystemError] = useState<null | string>(null);
  const errorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (systemError) {
      errorRef.current?.setAttribute('tabindex', '0');
      errorRef.current?.focus();
    }
  }, [systemError]);

  const onSubmitHandler = async (values: IPasswordValues): Promise<void> => {
    setIsLoading(true);
    setNeedFocus(true);
    setSystemError(null);
    const authValue: IAuthValues = {
      ...values,
      ...personalInfo,
    };

    try {
      await Auth.signUp({
        username: authValue.email,
        password: authValue.password,
        attributes: {
          email: authValue.email,
          given_name: authValue.firstName,
          family_name: authValue.lastName,
          'custom:primaryAccountType': PRIMARY_ACCOUNT_TYPE.WEBSITE_OWNER,
          'custom:emailNotification': `${authValue.emailService}`,
        },
      });
      navigate(PATHS.SEND_VERIFY_LINK, {
        state: {
          email: authValue.email,
          password: authValue.password,
        },
      });
    } catch (error) {
      if (error instanceof Error) {
        setSystemError(error.message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const onErrorHandler = (): void => {
    setNeedFocus(true);
    setSystemError(null);
  };

  return (
    <AuthFormHoc
      title={t('createPassword')}
      description={<p>{t('createPasswordAccount')}</p>}
      form={form}
      link={
        <Link navigateTo={PATHS.SIGN_UP} onClick={() => setIsPersonalInfoFormEnabled(false)}>
          <Flex>
            <ChevronLeft color="#5b5eff" />{' '}
            <Text color={'#000000'} fw={400}>
              {n('navBack')}
            </Text>
            <span className={styles['visually-hidden']}>{n('backToSignUp')}</span>
          </Flex>
        </Link>
      }
      isLoading={isLoading}
      submitText={`${t('confirm')}`}
      onSubmitHandler={onSubmitHandler}
      onErrorHandler={onErrorHandler}>
      <>
        {systemError && (
          <Flex ref={errorRef}>
            <Text color={'#a80000'}>{systemError}</Text>
          </Flex>
        )}
        <PasswordInput
          withAsterisk
          label={t('password')}
          placeholder={t('createPassword')}
          ariaLabel={`${t('passwordDescribe')}`}
          {...form.getInputProps('password')}
        />
        <Space h={'md'} />
        <PasswordInput
          withAsterisk
          label={`${t('confirm')} ${t('password')}`}
          placeholder={`${t('confirm')} ${t('password')}`}
          ariaLabel={`${t('confirmPasswordDescribe')}`}
          {...form.getInputProps('confirmPassword')}
        />
      </>
    </AuthFormHoc>
  );
};

export default PasswordForm;
